import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';

import { paths } from 'src/routes/paths';

import { CONFIG } from 'src/global-config';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: CONFIG.serverUrl });

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Spegnimento d'emergenza: il server risponde 503 "MANUTENZIONE" a ogni richiesta del consulente,
    // anche a chi era gia' loggato. Si mostra la pagina di manutenzione al posto di errori sparsi.
    if (
      error.response?.status === 503 &&
      error.response?.data?.error === 'MANUTENZIONE' &&
      !window.location.pathname.startsWith(paths.maintenance)
    ) {
      window.location.replace(paths.maintenance);
    }
    return Promise.reject((error.response && error.response.data) || 'Qualcosa è andato storto, riprova!');
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
// OK
  auth: {
    me: '/user/me',
    signIn: '/auth/signin',
    signUp: '/auth/signup',
    recoverPassword: '/auth/recoveryPassword',
    changePassword: '/auth/changePassword',
    generateTempJwt: '/auth/generateTempJwt'
  },
  user: {
    changeMyPassword: '/user/changeMyPassword',

    all: '/user/all',
    allclients: '/user/client/all',

    new: '/user',
    update: '/user',
    delete: '/user',
    importUsers: '/user/import',
    exportUsers: '/user/export',
    findUser: '/user/find',
    changeUserPassword: '/user/changeUserPassword',
    updateProfile: '/user/updateProfile',

    campiExtraList: '/user/campiExtra',
    addCampoExtra: '/user/campoExtra',
    updateCampoExtra: '/user/campoExtra',
    findCampoExtra:'/user/campoExtra',
    deleteCampoExtra:'/user/campoExtra'
  },
  consulente:{
    new: '/consulente',
    update: '/consulente',
    find: '/consulente/find',
    import: '/consulente/import',
    all: '/consulente/all',
    delete: '/consulente',
    export: '/consulente/export',
  },
  questionario: {
    all: '/questionario/all',
    get: '/questionario',
    delete: '/questionario',
    update: '/questionario',
    create: '/questionario',
    doAnswer: '/questionario/answer',
    lastAnswer: '/questionario/lastAnswer',
    updateAnswer: '/questionario/answer',
    getAnswer: '/questionario/answer',
    removeAnswer: '/questionario/answer',
    assegnaCliente: '/questionario/answer/assegna-cliente',
    listAnswers: '/questionario/answers',
    listAnswersByUser: '/questionario/answers/user',
    generatePdf: '/questionario/answer/pdf',
    doAnswerConsulente: '/questionario/answer/consulente',
    generatePdfFromHtml: '/questionario/pdf-from-html',
  },


  questionarioConsulente: {
    doAnswer: '/questionario/consulente/answer',
    updateAnswer: '/questionario/consulente/answer',
    getAnswer: '/questionario/consulente/answer',
    removeAnswer: '/questionario/consulente/answer',
    listAnswers: '/questionario/consulente/answers',
    generatePdf: '/questionario/consulente/answer/output',

  },


  notifications: {
    adminList: '/notifications/admin', // GET ?q=&type=&limit=&offset=
    create: '/notifications',          // POST
    remove: (id: string|number) => `/notifications/${id}`, // DELETE (se lo implementi)
  },
  group: {
    all: '/group/all',
    new: '/group',
    update: '/group',
    delete: '/group',
    find: '/group/find',
    userGrouped: '/group/userGrouped'
  },
  portafoglioModello: {
    list: '/portafogliomodello/modelli',
    status: '/portafogliomodello/status',
    sync: '/portafogliomodello/sync',
    extract: '/portafogliomodello/extract',
    resume: '/portafogliomodello/resume',
    uploadBacktest: '/portafogliomodello/upload-backtest',
    generate: '/portafogliomodello/generate',
    preview: (jobId: string, filename: string) => `/portafogliomodello/preview/${jobId}/${filename}`,
    publish: '/portafogliomodello/publish',
  },
  forecast: {
    stato: '/forecast/stato',
    compilazione: '/forecast/compilazione',
    impostazioni: '/forecast/impostazioni',
    boardGm: '/forecast/impostazioni/board-gm',
    boardAggregati: '/forecast/impostazioni/board-aggregati',
  },
  // contenuti del portale consulenti: dietro login e regola di visibilita' (gli endpoint /blog/* restano pubblici per l'app)
  portale: {
    articoli: '/portale/articoli',
    eventi: '/portale/eventi',
    corsoCrm: '/portale/corso-crm',
  },
  // incontri Business Training: lettura per Amministratore, Backoffice e Consulente; gestione solo staff
  businessTraining: {
    incontri: '/businessTraining/incontri',
    incontro: (id: string) => `/businessTraining/incontri/${encodeURIComponent(id)}`,
    allegati: (id: string) => `/businessTraining/incontri/${encodeURIComponent(id)}/allegati`,
    allegatoDelete: (id: string, allegatoId: string) =>
      `/businessTraining/incontri/${encodeURIComponent(id)}/allegati/${encodeURIComponent(allegatoId)}`,
    allegato: (id: string) => `/businessTraining/allegato/${encodeURIComponent(id)}`,
  },
  // permessi dei consulenti e spegnimento d'emergenza (solo Amministratore e Backoffice)
  permessi: {
    qualifiche: '/permessi/qualifiche',
    points: '/permessi/points',
    consulenti: '/permessi/consulenti',
    risorse: '/permessi/risorse',
    regola: '/permessi/regola',
    oscura: '/permessi/oscura',
    emergenza: '/permessi/emergenza',
    log: '/permessi/log',
  },
  // vista Beginner: /beginner/me/* per il beginner stesso, il resto solo Amministratore e Backoffice
  beginner: {
    me: '/beginner/me',
    documento: '/beginner/me/documento',
    scheda: '/beginner/me/scheda',
    esame: '/beginner/me/esame',
    videoVisto: '/beginner/me/video-visto',
    lezione: '/beginner/me/lezione',
    allegato: (chiave: string) => `/beginner/me/documento/${chiave}/allegato`,
    allegatoUrl: '/beginner/me/allegato/url',
    riferimenti: '/beginner/me/riferimenti',
    allegatiZip: '/beginner/me/allegati/zip',
    allegatoUrlStaff: (id: string | number) => `/beginner/${id}/allegato/url`,
    list: '/beginner',
    detail: (id: string | number) => `/beginner/${id}`,
    verificaDocumento: (id: string | number) => `/beginner/${id}/documento/verifica`,
    schedaAreaManager: (id: string | number) => `/beginner/${id}/scheda-area-manager`,
    promuovi: (id: string | number) => `/beginner/${id}/promuovi`,
    mondaySchede: '/beginner/monday/schede',
    mondayConsulenti: '/beginner/monday/consulenti',
    collegaMonday: (id: string | number) => `/beginner/${id}/monday`,
    mondaySync: (id: string | number) => `/beginner/${id}/monday/sync`,
    mondayFinecoCenter: '/beginner/monday/fineco-center',
  },
  // editor dei testi dei template PDF (solo Amministratore e Backoffice)
  testiPdf: {
    template: '/testi-pdf/template',
    pagina: '/testi-pdf/pagina',
    storico: '/testi-pdf/storico',
    anteprima: '/testi-pdf/anteprima',
    salva: '/testi-pdf/salva',
    ripristina: '/testi-pdf/ripristina',
  },
  stats: {
    get: '/stats/db',
    usageEvent: '/stats/usage/event',
    usageOverview: '/stats/usage/overview',
    usageByConsulente: '/stats/usage/byConsulente',
    usageByConsulenteExport: '/stats/usage/byConsulente/export',
    usageDetails: '/stats/usage/details',
    usageDetailsExport: '/stats/usage/details/export',
    usageTopContent: '/stats/usage/topContent',
    // app mobile (clienti e ospiti) e contatti raccolti
    appOverview: '/stats/app/overview',
    appClients: '/stats/app/clients',
    appInsights: '/stats/app/insights',
    appInactiveClients: '/stats/app/clients/inactive',
    appClientDetail: (idUser: number) => `/stats/app/clients/${idUser}`,
    appSlot: '/stats/app/insights/slot',
    appLeads: '/stats/app/leads',
    appLeadsExport: '/stats/app/leads/export',
  },

};
